import {
  type AgentDraft,
  type AgentDraftGoal,
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";

/**
 * Per-variable metadata the user can edit in the editor. Keyed by
 * variable name so an entry can survive a placeholder being temporarily
 * deleted and re-typed without losing the user's typed description /
 * default. Stale entries (whose name no longer matches any detected
 * placeholder) are dropped at save time by `buildVariables`.
 */
export interface VariableMeta {
  description: string;
  default: string;
}

/** Fixed description for the implicit `{{dataset.id}}` variable — the row
 *  is always present whenever a dataset goal is declared, and we don't
 *  let the user edit it because the meaning is invariant. */
export const IMPLICIT_DATASET_DESCRIPTION =
  "The dataset the agent will operate on. Selected at launch time.";

export interface DatasetSummaryGoalState {
  goal_type: "dataset_summary";
  /** Raw textarea contents; empty string means "omit on save and use the
   *  handler's opinionated default." Validated 1-4000 chars when non-empty
   *  to mirror :class:`DatasetSummaryAgentGoal.summary_format_spec_prompt`. */
  summary_format_spec_prompt: string;
}

export interface DatasetTriageGoalState {
  goal_type: "dataset_triage";
  /** Editable list rather than a dict so the user can reorder rows and
   *  the UI can render stable keys; converted to a ``Record`` on save. */
  label_vocabulary: Array<{ label: string; description: string }>;
}

export type GoalState = DatasetSummaryGoalState | DatasetTriageGoalState;

/**
 * Source of truth for the editor's form state.
 *
 * The editor stores everything the user can edit as plain text and
 * structured arrays here, then derives the wire-format
 * ``StartAgentSessionRequest`` and the declared-variables list at save
 * time. Keeping every text field in the form state (rather than an
 * already-built JSON dict) is what makes derived-variable detection
 * possible without re-parsing the body.
 */
export interface FormState {
  name: string;
  description: string;
  modelProfile: string | null;
  kickoffMessage: string;
  goals: GoalState[];
  /** User-editable description / default for each variable, keyed by name.
   *  The implicit `dataset.id` row's description is ignored here (see
   *  IMPLICIT_DATASET_DESCRIPTION). Stale keys (variables no longer
   *  referenced by any placeholder) are dropped on save. */
  variableMeta: Record<string, VariableMeta>;
}

/** The implicit, undeletable variable name injected into every dataset-typed
 *  goal at save time. Mirrored as a synthetic row in the variables panel. */
export const IMPLICIT_DATASET_VAR_NAME = "dataset.id";

/** Placeholder that replaces a dataset goal's ``dataset_id`` field. */
export const IMPLICIT_DATASET_PLACEHOLDER = `{{${IMPLICIT_DATASET_VAR_NAME}}}`;

/** Mirror of the server-side ``_PLACEHOLDER_RE`` so the form and the
 *  resolver agree on which substrings count as a variable reference. */
const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;

/**
 * Returns true when at least one declared goal carries an implicit
 * dataset slot. Currently every goal in the discriminated union is
 * dataset-typed, but the predicate is kept explicit so a future
 * non-dataset goal won't silently inject a ``{{dataset.id}}`` row.
 */
export function anyGoalUsesImplicitDataset(goals: GoalState[]): boolean {
  return goals.some(
    (g) =>
      g.goal_type === "dataset_summary" || g.goal_type === "dataset_triage",
  );
}

function pushPlaceholders(text: string, found: Set<string>): void {
  for (const match of text.matchAll(PLACEHOLDER_RE)) {
    found.add(match[1]);
  }
}

/**
 * Walks every text-bearing field in the form state and returns the set of
 * ``{{name}}`` placeholders. Mirrors the server's body walk but operates
 * on the structured form rather than the serialized JSON, so the variables
 * panel can update on every keystroke without re-running
 * ``buildRequestTemplate``.
 *
 * Identity fields (``name``, ``description``) are NOT walked because
 * they're agent metadata, not part of the request body that the resolver
 * sees. ``modelProfile`` is also skipped — it's a profile id selector,
 * not freeform text. Label-vocabulary *keys* are also skipped to match
 * the server's "values only" walk, which exists so a placeholder in a
 * key can't silently collide with another key after substitution.
 */
export function collectPlaceholders(formState: FormState): Set<string> {
  const found = new Set<string>();
  pushPlaceholders(formState.kickoffMessage, found);
  for (const goal of formState.goals) {
    if (goal.goal_type === "dataset_summary") {
      pushPlaceholders(goal.summary_format_spec_prompt, found);
    } else {
      for (const row of goal.label_vocabulary) {
        pushPlaceholders(row.description, found);
      }
    }
  }
  if (anyGoalUsesImplicitDataset(formState.goals)) {
    found.add(IMPLICIT_DATASET_VAR_NAME);
  }
  return found;
}

/**
 * Converts the placeholder set into a list of ``TemplateVariable``
 * declarations ready for the create / update wire payload. The
 * implicit dataset row gets :data:`TemplateVariableType.DATASET_ID` and
 * a fixed description; user-introduced variables get
 * :data:`TemplateVariableType.STRING` and pick up the editor-supplied
 * description / default from `variableMeta` (empty strings collapse to
 * `null` so they're omitted on the wire).
 *
 * The output is sorted so commits and snapshots are stable.
 */
export function buildVariables(formState: FormState): TemplateVariable[] {
  const placeholders = collectPlaceholders(formState);
  return [...placeholders].sort().map((name): TemplateVariable => {
    if (name === IMPLICIT_DATASET_VAR_NAME) {
      return {
        name,
        type: TemplateVariableType.DATASET_ID,
        description: IMPLICIT_DATASET_DESCRIPTION,
        required: true,
        default: null,
      };
    }
    const meta = formState.variableMeta[name];
    const description = meta?.description.trim() ?? "";
    const defaultValue = meta?.default ?? "";
    return {
      name,
      type: TemplateVariableType.STRING,
      description: description === "" ? null : description,
      required: true,
      default: defaultValue === "" ? null : defaultValue,
    };
  });
}

type RequestTemplateJson = Record<string, unknown>;

function buildGoalJson(goal: GoalState): Record<string, unknown> {
  if (goal.goal_type === "dataset_summary") {
    const summary: Record<string, unknown> = {
      goal_type: "dataset_summary",
      dataset_id: IMPLICIT_DATASET_PLACEHOLDER,
    };
    const trimmed = goal.summary_format_spec_prompt.trim();
    if (trimmed.length > 0) {
      summary.summary_format_spec_prompt = goal.summary_format_spec_prompt;
    }
    return summary;
  }
  const vocabulary: Record<string, string> = {};
  for (const row of goal.label_vocabulary) {
    if (row.label !== "") {
      vocabulary[row.label] = row.description;
    }
  }
  return {
    goal_type: "dataset_triage",
    dataset_id: IMPLICIT_DATASET_PLACEHOLDER,
    label_vocabulary: vocabulary,
  };
}

/**
 * Builds the request-body half of the wire payload: a JSON dict shaped
 * like :class:`StartAgentSessionRequest`. Omits every field the server
 * defaults sensibly (``system_prompt``, ``client_tools``, ``client_context``,
 * ``analysis_scope``, ``visibility``) so the editor never has to reason
 * about them.
 *
 * Returned value is a plain object suitable for ``JSON.stringify``;
 * callers should not mutate it.
 */
export function buildRequestTemplate(
  formState: FormState,
): RequestTemplateJson {
  const body: RequestTemplateJson = {
    goals: formState.goals.map(buildGoalJson),
    // Server-side `AgentTextContent` has only a `text` field — no
    // discriminator — so we send the bare shape rather than a fake
    // `type: "text"` tag the server ignores and that we'd then fail to
    // round-trip in `parseKickoffMessage`.
    messages:
      formState.kickoffMessage.length > 0
        ? [
            {
              role: "user",
              content: [{ text: formState.kickoffMessage }],
            },
          ]
        : [],
  };
  if (formState.modelProfile) {
    body.model_profile = formState.modelProfile;
  }
  return body;
}

interface SavedAgent {
  name: string;
  description?: string | null;
  request_template: Record<string, unknown>;
  variables: TemplateVariable[];
}

/**
 * Best-effort reverse of :func:`buildRequestTemplate` for edit mode.
 *
 * The new editor is the only thing that produces agents, so the
 * inputs are expected to look like what we'd write. Anything outside
 * that shape (legacy fields, future fields we don't handle) is silently
 * dropped — the user explicitly opted into a from-scratch rewrite and
 * we don't want stale payloads riding through unread.
 */
export function parseSavedAgent(saved: SavedAgent): FormState {
  const body = saved.request_template;
  return {
    name: saved.name,
    description: saved.description ?? "",
    modelProfile: parseModelProfile(body),
    kickoffMessage: parseKickoffMessage(body),
    goals: parseGoals(body),
    variableMeta: parseVariableMeta(saved.variables),
  };
}

/**
 * Build a {@link VariableMeta} entry from raw description + default
 * strings, applying the trim-then-collapse rule both code paths into
 * ``variableMeta`` share: trim the description, treat empty / missing
 * defaults as no default declared, and return ``null`` when the entry
 * carries no user-supplied content so the surrounding loops can skip it.
 *
 * Centralising this rule keeps edit-mode ({@link parseSavedAgent} via
 * {@link parseVariableMeta}) and create-from-draft ({@link
 * draftToFormState}) producing the same shape for the same logical
 * agent. Without it, e.g. a saved agent with a whitespace-only
 * description survived edit-mode parse while the equivalent draft was
 * dropped — different shapes for the same content.
 */
function normalizeVariableMeta(
  description: string | null | undefined,
  defaultValue: string | null | undefined,
): VariableMeta | null {
  const trimmedDescription = (description ?? "").trim();
  const value = defaultValue ?? "";
  if (trimmedDescription === "" && value === "") {
    return null;
  }
  return { description: trimmedDescription, default: value };
}

function parseVariableMeta(
  variables: TemplateVariable[],
): Record<string, VariableMeta> {
  const meta: Record<string, VariableMeta> = {};
  for (const variable of variables) {
    // Skip the implicit dataset row — its description is fixed and we
    // never want it appearing as user-editable metadata.
    if (variable.name === IMPLICIT_DATASET_VAR_NAME) {
      continue;
    }
    const entry = normalizeVariableMeta(variable.description, variable.default);
    if (entry !== null) {
      meta[variable.name] = entry;
    }
  }
  return meta;
}

function parseModelProfile(body: Record<string, unknown>): string | null {
  const value = body.model_profile;
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parseKickoffMessage(body: Record<string, unknown>): string {
  const messages = body.messages;
  if (!Array.isArray(messages) || messages.length === 0) {
    return "";
  }
  const first = messages[0] as Record<string, unknown> | undefined;
  if (first === undefined || !Array.isArray(first.content)) {
    return "";
  }
  // Server-side `AgentTextContent` is identified by the absence of a
  // `content_type` discriminator on its sibling content types. Pick the
  // first block that has a string `text` field and no other content type
  // tag — this is the canonical text block shape the SDK emits.
  for (const block of first.content) {
    if (typeof block !== "object" || block === null) {
      continue;
    }
    const record = block as Record<string, unknown>;
    if (record.content_type === undefined && typeof record.text === "string") {
      return record.text;
    }
  }
  return "";
}

/**
 * Maps an {@link AgentDraft} stashed by the ``client_create_agent`` client
 * tool into the editor's {@link FormState}.
 *
 * The two shapes are deliberately near-twins; this function exists so
 * the editor never has to peek at the draft's wire representation
 * (notably ``label_vocabulary`` is a ``Record`` in the draft and an
 * ordered array in the form). Variables that name the implicit dataset
 * placeholder are dropped because {@link buildVariables} injects that
 * row automatically; preserving an LLM-supplied entry would either
 * duplicate it or fight the fixed description in
 * {@link IMPLICIT_DATASET_DESCRIPTION}.
 */
export function draftToFormState(draft: AgentDraft): FormState {
  const goals: GoalState[] = (draft.goals ?? []).map(draftGoalToFormGoal);
  const variableMeta: Record<string, VariableMeta> = {};
  for (const variable of draft.variables ?? []) {
    if (variable.name === IMPLICIT_DATASET_VAR_NAME) {
      continue;
    }
    const entry = normalizeVariableMeta(variable.description, variable.default);
    if (entry !== null) {
      variableMeta[variable.name] = entry;
    }
  }
  return {
    name: draft.name ?? "",
    description: draft.description ?? "",
    modelProfile: draft.model_profile ?? null,
    kickoffMessage: draft.kickoff_message ?? "",
    goals,
    variableMeta,
  };
}

function draftGoalToFormGoal(goal: AgentDraftGoal): GoalState {
  if (goal.goal_type === "dataset_summary") {
    return {
      goal_type: "dataset_summary",
      summary_format_spec_prompt: goal.summary_format_spec_prompt ?? "",
    };
  }
  // dataset_triage: the draft's vocabulary is a Record so the LLM can
  // emit it as a plain JSON object. The editor stores rows as an array
  // to preserve order and stable React keys, so we flatten here.
  //
  // Defensive `?? {}`: `consumeAgentDraft` already drops triage goals
  // whose `label_vocabulary` isn't a plain object, so we should never
  // see a malformed value here. The fallback exists in case a future
  // producer bypasses the validator and writes a draft directly — the
  // editor's `useState` initializer is a bad place to throw because
  // there's no error boundary above it.
  return {
    goal_type: "dataset_triage",
    label_vocabulary: Object.entries(goal.label_vocabulary ?? {}).map(
      ([label, description]) => ({ label, description }),
    ),
  };
}

function parseGoals(body: Record<string, unknown>): GoalState[] {
  const goals = body.goals;
  if (!Array.isArray(goals)) {
    return [];
  }
  const parsed: GoalState[] = [];
  for (const raw of goals) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const obj = raw as Record<string, unknown>;
    if (obj.goal_type === "dataset_summary") {
      parsed.push({
        goal_type: "dataset_summary",
        summary_format_spec_prompt:
          typeof obj.summary_format_spec_prompt === "string"
            ? obj.summary_format_spec_prompt
            : "",
      });
    } else if (obj.goal_type === "dataset_triage") {
      const vocab = obj.label_vocabulary;
      const rows: Array<{ label: string; description: string }> = [];
      if (
        typeof vocab === "object" &&
        vocab !== null &&
        !Array.isArray(vocab)
      ) {
        for (const [label, description] of Object.entries(
          vocab as Record<string, unknown>,
        )) {
          if (typeof description === "string") {
            rows.push({ label, description });
          }
        }
      }
      parsed.push({ goal_type: "dataset_triage", label_vocabulary: rows });
    }
  }
  return parsed;
}
